import { Component, inject, input, output, TemplateRef } from '@angular/core';
import { TypeAheadSelector2Component } from 'src/app/shared/components/type-ahead-selector2/type-ahead-selector2.component';
import { map, Observable } from 'rxjs';
import { PricePolicyType, PricePolicy, PricePolicySearchEvent } from '../../interfaces/price-policy';
import { PricePolicyV2Service } from 'src/app/shared/services/price-policy-v2.service';
import { PagedResult } from 'src/app/shared/models/paging';
import { PageEvent } from '@angular/material/paginator';

export interface PricePolicyFallbackConfig {
  isGlobal: boolean;
  organizationId: number | null;
  pricePolicyId: number | null;
  currentPricePolicyId: number | null;
}

@Component({
  selector: 'adm-price-policy-fallback',
  templateUrl: './price-policy-fallback.component.html',
  styleUrl: './price-policy-fallback.component.scss',
  imports: [TypeAheadSelector2Component],
})
export class AdmPricePolicyFallbackComponent {
  pricePolicyV2Service = inject(PricePolicyV2Service);

  config = input<PricePolicyFallbackConfig>({
    isGlobal: true,
    organizationId: null,
    pricePolicyId: null,
    currentPricePolicyId: null,
  });
  itemTemplate = input<TemplateRef<any>>();

  disabled = input<boolean>(false);
  resetTrigger = input<{ reset: boolean }>({ reset: false });

  itemSelected = output<PricePolicy | null>();

  fetchPricePolicies = (term: string): Observable<PricePolicy[]> => {
    const pageEvent: PageEvent = {
      pageIndex: 0,
      pageSize: 25,
      length: 0,
    };

    const searchEvent = new PricePolicySearchEvent(pageEvent);
    searchEvent.query = term.length > 0 ? term : null;
    searchEvent.type = PricePolicyType.FixedWithOkMeter;

    // Set query parameters based on affiliation
    const { isGlobal, organizationId, currentPricePolicyId } = this.config();
    if (isGlobal) {
      searchEvent.onlyGlobal = true;
      searchEvent.includeGlobal = false;
      searchEvent.organizationId = null;
    } else {
      if (organizationId) {
        searchEvent.organizationId = organizationId;
        searchEvent.includeGlobal = true;
        searchEvent.onlyGlobal = false;
      }
    }

    return this.pricePolicyV2Service.getPricePolicies(searchEvent).pipe(
      map((result: PagedResult<PricePolicy>) => {
        // Filter out the current price policy being edited
        return currentPricePolicyId ? result.items.filter(item => item.id !== currentPricePolicyId) : result.items;
      })
    );
  };

  // Fetch by ID using the list endpoint to avoid 403 errors
  fetchPricePolicyById = (id: string): Observable<PricePolicy | null> => {
    const pageEvent: PageEvent = {
      pageIndex: 0,
      pageSize: 25,
      length: 0,
    };

    const searchEvent = new PricePolicySearchEvent(pageEvent);
    searchEvent.query = null; // Fetch all to find the specific ID

    // Set query parameters based on affiliation
    const { isGlobal, organizationId, currentPricePolicyId } = this.config();
    if (isGlobal) {
      searchEvent.onlyGlobal = true;
      searchEvent.includeGlobal = false;
      searchEvent.organizationId = null;
    } else {
      if (organizationId) {
        searchEvent.organizationId = organizationId;
        searchEvent.includeGlobal = true;
        searchEvent.onlyGlobal = false;
      }
    }

    return this.pricePolicyV2Service.getPricePolicies(searchEvent).pipe(
      map((result: PagedResult<PricePolicy>) => {
        const foundPolicy = result.items.find(item => item.id === parseInt(id));

        // Don't return the policy if it's the one being edited
        if (foundPolicy && currentPricePolicyId && foundPolicy.id === currentPricePolicyId) {
          return null;
        }

        return foundPolicy ?? null;
      })
    );
  };

  pricePolicySelected = (pricePolicy: PricePolicy | null | undefined) => {
    this.itemSelected.emit(pricePolicy ?? null);
  };
}
